<template>
  <div class="account-panel">
    <button
      type="button"
      class="account-panel__trigger"
      @click="open"
    >
      <svg class="account-panel__icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </svg>
      <span class="account-panel__label-wrap">
        <span class="account-panel__label">Account</span>
        <span v-if="email" class="account-panel__email">{{ email }}</span>
      </span>
    </button>

    <Dialog
      v-model:visible="visible"
      modal
      dismissable-mask
      :style="dialogStyle"
    >
      <template #header>
        <h2 class="ds-heading account-panel__title">
          {{ showPlans ? "Choose a plan" : "Account" }}
        </h2>
      </template>

      <div v-if="loading" class="account-panel__state">
        <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
      </div>

      <div v-else-if="error || !summary" class="account-panel__state">
        <p class="ds-error">We couldn't load your account. Please try again.</p>
      </div>

      <template v-else-if="showPlans">
        <button
          type="button"
          class="ds-link account-panel__back"
          @click="showPlans = false"
        >
          ← Back to account
        </button>
        <PricingPlans />
      </template>

      <div v-else class="account-panel__body">
        <section class="account-section">
          <h3 class="account-section__title">Subscription</h3>

          <div class="account-field">
            <span class="account-field__key">Email</span>
            <span class="account-field__val">{{ summary.email }}</span>
          </div>

          <template v-if="summary.status === 'paid'">
            <div class="account-field">
              <span class="account-field__key">Plan</span>
              <span class="account-field__val">{{ planLabel }}</span>
            </div>
            <div v-if="priceLabel" class="account-field">
              <span class="account-field__key">Price</span>
              <span class="account-field__val">{{ priceLabel }}</span>
            </div>
            <div v-if="nextBillingLabel" class="account-field">
              <span class="account-field__key">Renews</span>
              <span class="account-field__val">{{ nextBillingLabel }}</span>
            </div>
            <!-- Members on a comped/free plan have an ACTIVE plan connection but
                 no Stripe customer, so the hosted portal would error for them. -->
            <button
              v-if="summary.stripeCustomerId"
              type="button"
              class="ds-button ds-button--secondary account-panel__action"
              :disabled="busy"
              @click="manageBilling"
            >
              Manage billing
            </button>
          </template>

          <template v-else-if="summary.status === 'trial'">
            <div class="account-field">
              <span class="account-field__key">Status</span>
              <span class="account-field__val">
                Free trial — {{ trialDaysLeft }}
                {{ trialDaysLeft === 1 ? "day" : "days" }} left
              </span>
            </div>
            <button
              type="button"
              class="ds-button ds-button--primary account-panel__action"
              @click="showPlans = true"
            >
              Subscribe
            </button>
          </template>

          <template v-else>
            <div class="account-field">
              <span class="account-field__key">Status</span>
              <span class="account-field__val">No active subscription</span>
            </div>
            <button
              type="button"
              class="ds-button ds-button--primary account-panel__action"
              @click="showPlans = true"
            >
              View plans
            </button>
          </template>
        </section>

        <TeamSection :team-id="summary.ownedTeamId" />

        <div class="account-panel__footer">
          <button
            type="button"
            class="ds-button ds-button--ghost"
            :disabled="busy"
            @click="doLogout"
          >
            Log out
          </button>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import {
  getAccountSummary,
  launchBillingPortal,
  logout,
} from "../auth";
import PricingPlans from "./PricingPlans.vue";
import TeamSection from "./TeamSection.vue";

export default {
  name: "AccountPanel",
  components: { PricingPlans, TeamSection },
  data() {
    return {
      visible: false,
      loading: false,
      error: false,
      busy: false,
      summary: null,
      showPlans: false,
    };
  },
  computed: {
    email() {
      return this.summary?.email ?? "";
    },
    dialogStyle() {
      return this.showPlans
        ? { width: "92vw", maxWidth: "880px" }
        : { width: "92vw", maxWidth: "480px" };
    },
    planLabel() {
      const p = this.summary?.plan;
      if (!p) return "";
      return p.planName && p.tierName
        ? `${p.planName} — ${p.tierName}`
        : p.tierName || p.planName || "";
    },
    priceLabel() {
      const p = this.summary?.plan;
      if (!p || p.amount == null) return "";
      return `${this.formatMoney(p.amount, p.currency)} / ${this.intervalLabel(
        p.interval
      )}`;
    },
    nextBillingLabel() {
      const ts = this.summary?.nextBillingDate;
      if (!ts) return "";
      // Memberstack returns a Unix timestamp in seconds.
      const date = new Date(ts * 1000);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    trialDaysLeft() {
      const raw = this.summary?.trialEndsAt;
      if (!raw) return 0;
      const ms = new Date(raw).getTime() - Date.now();
      return Math.max(0, Math.ceil(ms / 86400000));
    },
  },
  async mounted() {
    await this.loadSummary();
  },
  methods: {
    async loadSummary() {
      this.loading = true;
      this.error = false;
      try {
        this.summary = await getAccountSummary();
        if (!this.summary) this.error = true;
      } catch (err) {
        console.error("[account] load failed:", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async open() {
      this.visible = true;
      this.showPlans = false;
      // Refresh quietly — data is already present from mount.
      if (!this.summary) await this.loadSummary();
    },
    formatMoney(amount, currency) {
      // Memberstack amounts are in whole currency units (not cents).
      const value = Number(amount);
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: (currency || "usd").toUpperCase(),
          minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        }).format(value);
      } catch (err) {
        return `$${value}`;
      }
    },
    intervalLabel(type) {
      const map = {
        daily: "day",
        weekly: "week",
        monthly: "month",
        yearly: "year",
      };
      const key = (type || "").toLowerCase();
      return map[key] || key || "term";
    },
    async manageBilling() {
      this.busy = true;
      try {
        // Auto-redirects to Stripe's hosted portal.
        await launchBillingPortal();
      } catch (err) {
        console.error("[account] billing portal failed:", err);
        this.busy = false;
        window.alert("We couldn't open the billing portal. Please try again.");
      }
    },
    async doLogout() {
      this.busy = true;
      try {
        await logout();
      } catch (err) {
        console.error("[account] logout failed:", err);
      }
      // Full reload returns to a clean, signed-out state (shows the auth panel).
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.account-panel__trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--font-sans);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.account-panel__trigger:hover {
  background: var(--surface-50);
  border-color: var(--mute);
}

.account-panel__icon {
  width: 18px;
  height: 18px;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.account-panel__label-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  line-height: 1.2;
}

.account-panel__label {
  font-size: 0.82rem;
  font-weight: 600;
}

.account-panel__email {
  font-size: 0.72rem;
  color: var(--mute);
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-panel__title {
  margin: 0;
}

.account-panel__state {
  display: flex;
  justify-content: center;
  padding: 28px 0;
}

.account-panel__body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.account-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.account-section__title {
  font-family: var(--font-sans);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mute);
  margin: 0;
}

.account-field {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.9rem;
}

.account-field__key {
  color: var(--mute);
  flex: none;
}

.account-field__val {
  color: var(--ink);
  text-align: right;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-panel__action {
  align-self: flex-start;
  margin-top: 4px;
}

.account-panel__footer {
  border-top: 1px solid var(--line);
  padding-top: 16px;
}

.account-panel__back {
  display: inline-block;
  margin-bottom: 16px;
  font-size: 0.85rem;
}
</style>
